import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, Shield, Copy, Plus, X, Search, UserPlus, Link2, User, Users } from 'lucide-react';
import { groupsAPI, closeCircleAPI, connectionsAPI } from '../api/client';
import Layout from '../components/Layout';
import '../styles/groups.css';

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  return (parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : name[0]).toUpperCase();
}

function Toggle({ on, onChange }) {
  return (
    <button className={`gc-toggle${on ? ' on' : ''}`} onClick={() => onChange(!on)} aria-pressed={on}>
      <span className="gc-toggle-thumb" />
    </button>
  );
}

function AddPeopleSheet({ group, onClose, onAdded }) {
  const [connections, setConnections] = useState([]);
  const [members, setMembers] = useState(null); // null = not yet loaded
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(null);
  const [added, setAdded] = useState(new Set());
  const currentUserId = localStorage.getItem('userId');

  useEffect(() => {
    Promise.all([
      connectionsAPI.getAll(),
      groupsAPI.getMembers(group.id),
    ]).then(([cRes, mRes]) => {
      setConnections((cRes.data || []).filter(c => c.status === 'accepted'));
      setMembers(new Set((mRes.data || []).map(m => m.id)));
    }).catch(() => { setMembers(new Set()); });
  }, [group.id]);

  const getFriendId = (c) => c.user_id === currentUserId ? c.connected_user_id : c.user_id;

  const filtered = connections.filter(c => {
    const name = (c.display_name || '').toLowerCase();
    return name.includes(search.toLowerCase());
  });

  const handleAdd = async (friendId) => {
    setAdding(friendId);
    try {
      await groupsAPI.addMember(group.id, friendId);
      setAdded(prev => new Set([...prev, friendId]));
      onAdded();
    } catch {}
    setAdding(null);
  };

  const isMember = (id) => (members && members.has(id)) || added.has(id);

  return (
    <div className="gc-sheet-overlay" onClick={onClose}>
      <div className="gc-sheet" onClick={e => e.stopPropagation()}>
        <div className="gc-sheet-header">
          <h3 className="gc-sheet-title">Add People to {group.name}</h3>
          <button className="gc-sheet-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="gc-sheet-search">
          <Search size={15} color="#888" />
          <input
            className="gc-sheet-input"
            placeholder="Search connections…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
          />
        </div>
        <div className="gc-sheet-list">
          {members === null ? (
            <p className="gc-sheet-empty">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="gc-sheet-empty">
              {connections.length === 0 ? 'No connections yet.' : 'No results.'}
            </p>
          ) : filtered.map(c => {
            const friendId = getFriendId(c);
            const alreadyIn = isMember(friendId);
            return (
              <div key={c.id} className="gc-sheet-row">
                <div className="gc-sheet-avatar">{getInitials(c.display_name)}</div>
                <span className="gc-sheet-name">{c.display_name}</span>
                {alreadyIn ? (
                  <span className="gc-sheet-in-badge">In group</span>
                ) : (
                  <button
                    className="gc-sheet-add-btn"
                    onClick={() => handleAdd(friendId)}
                    disabled={adding === friendId}
                  >
                    {adding === friendId ? '…' : <><UserPlus size={13} /> Add</>}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function Groups() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [circle, setCircle] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedGroupId, setCopiedGroupId] = useState(null);
  const [addSheet, setAddSheet] = useState(null); // group object

  const load = () => Promise.all([
    groupsAPI.getAll().then(r => setGroups(r.data || [])),
    closeCircleAPI.getAll().then(r => setCircle(r.data || [])),
  ]).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const handleCopyGroupLink = async (groupId) => {
    try {
      const res = await groupsAPI.generateInviteLink(groupId);
      await navigator.clipboard.writeText(res.data.invite_url).catch(() => {});
      setCopiedGroupId(groupId);
      setTimeout(() => setCopiedGroupId(null), 2000);
    } catch {}
  };

  const handleToggleSharing = async (groupId, enabled) => {
    setGroups(prev => prev.map(g => g.id === groupId ? { ...g, sharing_enabled: enabled ? 1 : 0 } : g));
    await groupsAPI.toggleSharing(groupId, enabled).catch(() => {});
  };

  return (
    <Layout title="Groups & Circles">
      <div className="gc-page">
        <div className="gc-main">

          {/* Page header */}
          <div className="gc-page-header">
            <div>
              <h1 className="gc-page-title">Groups &amp; Circles</h1>
              <p className="gc-page-sub">Organize your social landscape. Define who sees your availability.</p>
            </div>
            <div className="gc-header-actions">
              <button className="gc-find-friends-btn" onClick={() => navigate('/friends')}>
                <Users size={15} strokeWidth={2.5} /> Friends
              </button>
              <button className="gc-create-btn" onClick={() => navigate('/create-group')}>
                <Plus size={15} strokeWidth={2.5} /> Create New Group
              </button>
            </div>
          </div>

          {loading ? (
            <div className="gc-loading">Loading…</div>
          ) : (
            <>
              {/* Close Circle */}
              <section className="gc-section">
                <div className="gc-section-header">
                  <span className="gc-section-title">Close Circle</span>
                  <button className="gc-manage-btn" onClick={() => navigate('/close-circle')}>Manage</button>
                </div>
                <p className="gc-section-desc">Trusted friends whose availability you can see on your calendar.</p>
                {circle.length === 0 ? (
                  <div className="gc-groups-list">
                    <div className="gc-group-row gc-empty-row">
                      <span className="gc-empty-text">No members yet — <button className="gc-inline-link" onClick={() => navigate('/close-circle')}>add friends to your Close Circle</button></span>
                    </div>
                  </div>
                ) : (
                  <div className="gc-groups-list">
                    {circle.map(m => (
                      <div key={m.id} className="gc-group-row" style={{ cursor: 'pointer' }} onClick={() => navigate(`/profile/${m.trusted_user_id}`)}>
                        <div className="gc-group-icon" style={{ background: '#52AAD8' }}><User size={18} /></div>
                        <div className="gc-group-info">
                          <span className="gc-group-name">{m.display_name}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Friend Groups */}
              <section className="gc-section">
                <div className="gc-section-header">
                  <span className="gc-section-title">Friend Groups</span>
                </div>
                <p className="gc-section-desc">Groups for organizing plans together.</p>
                {groups.length === 0 ? (
                  <div className="gc-groups-list">
                    <div className="gc-group-row gc-empty-row">
                      <span className="gc-empty-text">No groups yet — <button className="gc-inline-link" onClick={() => navigate('/create-group')}>create one to coordinate with your crew</button></span>
                    </div>
                  </div>
                ) : (
                  <div className="gc-groups-list">
                    {groups.map(group => (
                      <div key={group.id} className="gc-group-row">
                        <div className="gc-group-icon" style={{ background: '#52AAD8' }}>
                          <Users size={18} />
                        </div>
                        <div className="gc-group-info" onClick={() => navigate(`/group/${group.id}`)}>
                          <span className="gc-group-name">{group.name}</span>
                          <span className="gc-group-meta">{group.member_count || 0} Members</span>
                        </div>
                        <div className="gc-group-controls">
                          <button
                            className="gc-add-people-btn"
                            onClick={() => setAddSheet(group)}
                            title="Add people"
                          >
                            <UserPlus size={15} />
                            Add
                          </button>
                          <button
                            className="gc-add-people-btn"
                            onClick={() => handleCopyGroupLink(group.id)}
                            title="Copy invite link"
                          >
                            <Link2 size={14} />
                            {copiedGroupId === group.id ? 'Copied!' : 'Link'}
                          </button>
                          <div className="gc-sharing-control">
                            <span className="gc-sharing-label">SHARING</span>
                            <Toggle
                              on={!!group.sharing_enabled}
                              onChange={(v) => handleToggleSharing(group.id, v)}
                            />
                          </div>
                          <button className="gc-gear-btn" onClick={() => navigate(`/group/${group.id}`)}>
                            <Settings size={16} color="#888" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </div>

        {/* Right sidebar */}
        <div className="gc-sidebar">
          <div className="gc-invite-card">
            <h3 className="gc-invite-title">Invite to a group</h3>
            <p className="gc-invite-sub">Click "Link" on any group to copy a shareable invite link. Anyone with the link can join.</p>
          </div>

          <div className="gc-privacy-card">
            <div className="gc-privacy-header">
              <Shield size={16} color="#52AAD8" />
              <span className="gc-privacy-title">Privacy First</span>
            </div>
            <p className="gc-privacy-body">
              Each group only sees the specific 'Free' blocks you choose to share with them. Your private calendar events are never visible.
            </p>
          </div>
        </div>
      </div>

      {addSheet && (
        <AddPeopleSheet
          group={addSheet}
          onClose={() => setAddSheet(null)}
          onAdded={() => { load(); }}
        />
      )}
    </Layout>
  );
}

